/* eslint-disable react/no-children-prop */
import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import {
    IconButton, Badge, Popover, makeStyles, Button, Tooltip,
    Typography, Divider, ListItem, ListItemIcon, ListItemText, List,
} from '@material-ui/core';
import DateUtils, { DateFormat } from 'lib/DateUtils';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import { isEmpty, isFinite } from 'lodash';
import KeyStore from 'utils/KeyStore';
import { modules, COLLECTIONS_SUBMODULES } from 'utils/enum/modules';
import MessageUtils from 'utils/MessageUtils';
import CallIcon from '@material-ui/icons/Call';
import InboxIcon from '@material-ui/icons/Inbox';
import TodayIcon from '@material-ui/icons/Today';
import If from 'components/widgets/conditional/If';
import { EventColors } from 'utils/enum/ActivitiesEnum';
import { icons } from 'components/modules/lead/LeadHelper';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { ReactComponent as SMSIcon } from 'assets/crm/sms.svg';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';

// Services
import { FetchPolicy, NOTIFICATION_TYPE } from 'utils/enum/Core';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import NotificationQuery from 'services/graphQL/query/core/NotificationQuery';
import UserNotificationQuery from 'services/graphQL/query/core/UserNotificationQuery';
import UserNotificationMutation from 'services/graphQL/mutate/core/UserNotificationMutation';
import NotificationSubscription from 'services/graphQL/subscription/core/NotificationSubscription';
import UserNotificationSubscription from 'services/graphQL/subscription/core/UserNotificationSubscription';
import AppReleaseQuery from 'services/graphQL/query/core/AppReleaseQuery';
import AppReleaseMutate from 'services/graphQL/mutate/core/AppReleaseMutate';
import AppReleaseSubscription from 'services/graphQL/subscription/core/AppReleaseSubscription';
import AppReleaseDialog from 'components/layout/AppReleaseDialog';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import moment from 'moment';

const initState = {
    notificationCount: 0,
    records: [],
};
const TYPE = {
    COMMUNICATION: 'COMMUNICATION',
    NOTIFICATION: 'NOTIFICATION',
};

const ACTION_TYPE = {
    ADD_NOTIFICATION: 'addNotification',
    SET_NOTIFICATION: 'setNotification',
    UPDATE_NOTIFICATION: 'updateNotification',
    SET_NOTIFICATION_COUNT: 'setNotificationCount',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.SET_NOTIFICATION_COUNT:
        return update(state, {
            notificationCount: { $set: action.payload },
        });
    case ACTION_TYPE.SET_NOTIFICATION:
        return update(state, {
            records: { $set: action.payload },
        });
    case ACTION_TYPE.ADD_NOTIFICATION:
        return update(state, {
            records: { $unshift: [action.payload] },
        });
    case ACTION_TYPE.UPDATE_NOTIFICATION:
        const record = action.payload;
        const index = state.records.findIndex((item) => item.notificationId === record.notificationId);

        if (index >= 0) {
            const recordsUpdated = update(state.records, { [index]: { $set: record } });

            return update(state, {
                records: { $set: recordsUpdated },
            });
        }

        return state;
    default:
        return state;
    }
};

const NotificationBadge = ({ type, iconColor, collectionCount = 0 }) => {
    const classes = makeStyles((theme) => ({
        popover: {
            minHeight: 60,
            width: 360,
            maxHeight: '80%',
            display: 'flex',
            overflow: 'hidden',
            flexDirection: 'column',
            borderRadius: theme.spacing(1),
        },
        notificationList: {
            overflow: 'auto',
        },
        bodyMessage: {
            position: 'relative',
            width: '100%',
        },
        titleItem: {
            textTransform: 'capitalize',
        },
        opportunityAssignIcon: {
            fill: theme.palette.text.link,
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(1),
        },
        textPrimary: {
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
        },
        messageUnread: {
            width: 8,
            height: 8,
            display: 'flex',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgb(24, 144, 255)',
        },
        divider: {
            borderStyle: 'dashed',
            borderWidth: '0px 0px thin',
            borderColor: 'rgba(145, 158, 171, 0.24)',
        },
        footerItem: {
            color: 'rgb(145, 158, 171)',
            display: 'flex',
            alignItems: 'center',
            '& svg': {
                fontSize: theme.spacing(2),
                marginRight: theme.spacing(1),
            },
        },
        textMessage: {
            fontWeight: 400,
            color: 'rgb(99, 115, 129)',
            margin: theme.spacing(0.5, 0),
            display: '-webkit-box',
            maxWidth: '100%',
            '-webkit-line-clamp': '4',
            '-webkit-box-orient': 'vertical',
            overflow: 'hidden',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
        },
        containerSMSNotification: {
            fontSize: '14px',
            textDecoration: 'none',
            color: '#959595',
            borderBottom: '1px solid #959595',
            display: 'block',
            padding: theme.spacing(1.5, 0.8),
        },
        smsNotificationHeader: {
            fontWeight: 'bold',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            '& svg': {
                marginRight: theme.spacing(1),
            },
            '& svg g path': {
                fill: '#959595',
            },
        },
        smsNotificationBody: {
            fontSize: '13px',
            display: '-webkit-box',
            maxWidth: '100%',
            '-webkit-line-clamp': '4',
            '-webkit-box-orient': 'vertical',
            overflow: 'hidden',
        },
        alignCenter: {
            textAlign: 'center',
        },
        todayIcon: {
            color: EventColors.SCHEDULED.backgroundColor,
        },
        callIcon: {
            color: EventColors.CONFIRMED.color,
        },
        taskIcon: {
            color: EventColors.SHOW.color,
        },
        link: {
            textDecoration: 'none',
            color: 'initial',
        },
        button: {
            padding: '6px',
            '& svg': {
                fill: iconColor,
            },
        },
    }))();

    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const [state, dispatch] = useReducer(reducer, initState);
    const { data: subscriptionNotificationData } = useSubscription(NotificationSubscription.NOTIFICATION_UPDATED, { variables: { type } });
    const { data: subscriptionData } = useSubscription(UserNotificationSubscription.USER_NOTIFICATION_COUNT_UPDATED, { variables: { type } });
    const [markUserNotificationAsRead] = useMutation(UserNotificationMutation.MARK_USER_NOTIFICATION_AS_READ_BY_NOTIFICATION_ID, { variables: { type } });
    const { data } = useQuery(UserNotificationQuery.GET_UNREAD_USER_NOTIFICATION_COUNT,
        { fetchPolicy: FetchPolicy.NETWORK_ONLY, variables: { type } });
    const { data: notificationData, refetch } = useQuery(NotificationQuery.GET_NOTIFICATION_BY_USER,
        { fetchPolicy: FetchPolicy.NETWORK_ONLY, variables: { type } });
    const [markUserNotificationByTypeAsRead, { loading: updating }] = useMutation(UserNotificationMutation.MARK_USER_NOTIFICATION_BY_TYPE_AS_READ,
        { variables: { type } });
    const isCommunication = type?.toUpperCase() === TYPE.COMMUNICATION;

    const [openAppReleaseDialog, setOpenAppReleaseDialog] = useState(false);

    const { data: appReleaseData, refetch: refetchAppReleases } = useQuery(AppReleaseQuery.GET_UNREAD_APP_RELEASES, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: isCommunication,
    });
    const [markAllAppReleasesAsRead] = useMutation(AppReleaseMutate.MARK_ALL_APP_RELEASES_AS_READ);
    const { data: subscriptionAppReleaseData } = useSubscription(AppReleaseSubscription.APP_RELEASE_ACTIVATED, { skip: isCommunication });
    const unreadAppReleases = appReleaseData?.getUnreadAppReleases ?? [];

    const uniqueAppReleaseTypes = React.useMemo(() => {
        if (!unreadAppReleases || unreadAppReleases.length === 0) return [];
        return [...new Set(unreadAppReleases.map((r) => r.type?.toUpperCase()))].filter(Boolean);
    }, [unreadAppReleases]);

    const latestAppRelease = React.useMemo(() => {
        if (!unreadAppReleases || unreadAppReleases.length === 0) return null;
        return unreadAppReleases.reduce((prev, current) => {
            const prevDate = new Date(prev.modifiedOn || prev.releaseDate);
            const currDate = new Date(current.modifiedOn || current.releaseDate);
            return prevDate > currDate ? prev : current;
        });
    }, [unreadAppReleases]);

    useEffect(() => {
        if (subscriptionAppReleaseData?.appReleaseActivated) {
            refetchAppReleases();
        }
    }, [subscriptionAppReleaseData, refetchAppReleases]);

    const emptyList = state.records.length === 0 && (isCommunication || unreadAppReleases.length === 0);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const onError = () => {
        ModalUtils.errorMessage(null, MessageUtils.getGenericError('updating', 'Notification'));
    };

    const markAllAsRead = async () => {
        try {
            const promises = [markUserNotificationByTypeAsRead()];

            if (unreadAppReleases.length > 0) {
                promises.push(markAllAppReleasesAsRead());
            }

            const results = await Promise.all(promises);
            const userNotificationResponse = results[0];

            if (userNotificationResponse?.data?.markUserNotificationByTypeAsRead) {
                refetch();
                if (unreadAppReleases.length > 0) {
                    refetchAppReleases();
                }
                setAnchorEl(null);
            } else {
                onError();
            }
        } catch (ex) {
            onError();
        }
    };

    const getIcon = (value) => {
        switch (value?.toUpperCase()) {
        case NOTIFICATION_TYPE.SMS_RECEIVED:
            return <SMSIcon />;
        case NOTIFICATION_TYPE.EMAIL_RECEIVED:
            return icons.email;
        case NOTIFICATION_TYPE.REMINDER_EVENT:
            return <TodayIcon fontSize="small" className={classes.todayIcon} />;
        case NOTIFICATION_TYPE.REMINDER_CALL:
            return <CallIcon fontSize="small" className={classes.callIcon} />;
        case NOTIFICATION_TYPE.REMINDER_TASK:
            return <AssignmentTurnedInIcon fontSize="small" className={classes.taskIcon} />;
        case NOTIFICATION_TYPE.OPPORTUNITY_ASSIGN:
        case NOTIFICATION_TYPE.OPPORTUNITY_UPDATE:
            return <AssignmentIndIcon fontSize="small" className={classes.opportunityAssignIcon} />;
        default:
            return null;
        }
    };

    const onClick = async (record, isPerformanceReport = false) => {
        try {
            const { opportunityId } = record.data;

            if (!record.userNotification.read) {
                await markUserNotificationAsRead({ variables: { notificationId: record.notificationId } });
            }
            setAnchorEl(null);
            if (!isPerformanceReport) history.push(`/${modules.OPPORTUNITIES}/${opportunityId}`, record);
        } catch (ex) {
            onError();
        }
    };

    useEffect(() => {
        if (isFinite(subscriptionData?.userNotificationCountUpdated)) {
            dispatch({
                type: ACTION_TYPE.SET_NOTIFICATION_COUNT,
                payload: subscriptionData.userNotificationCountUpdated,
            });
        }
    }, [subscriptionData]);

    useEffect(() => {
        const result = subscriptionNotificationData?.notificationUpdated;
        if (!isEmpty(result)) {
            if (result.type === SubscriptionActionType.ADDED) {
                dispatch({ type: ACTION_TYPE.ADD_NOTIFICATION, payload: result.notification });
                return;
            }

            if (result.type === SubscriptionActionType.UPDATED) {
                dispatch({ type: ACTION_TYPE.UPDATE_NOTIFICATION, payload: result.notification });
            }
        }
    }, [subscriptionNotificationData]);

    useEffect(() => {
        if (isFinite(data?.getUnreadUserNotificationCount)) {
            dispatch({
                type: ACTION_TYPE.SET_NOTIFICATION_COUNT,
                payload: data.getUnreadUserNotificationCount,
            });
        }
    }, [data]);

    useEffect(() => {
        if (!isEmpty(notificationData?.getNotificationByUser)) {
            dispatch({
                type: ACTION_TYPE.SET_NOTIFICATION,
                payload: notificationData.getNotificationByUser,
            });
        }
    }, [notificationData]);

    useEffect(() => {
        if (!isCommunication && unreadAppReleases.length > 0) {
            const keyStore = new KeyStore();
            const hasAutoOpened = keyStore.getAppReleaseAutoOpened();
            if (!hasAutoOpened) {
                setOpenAppReleaseDialog(true);
                keyStore.setAppReleaseAutoOpened(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReleaseData]);

    const getNotificationTitle = ({
        itemType,
        isCommunicationType,
        customerName,
        recordType,
    }) => {
        if (isCommunicationType) {
            return `You have new message from ${customerName}`;
        }

        switch (itemType) {
        case NOTIFICATION_TYPE.CREATE_REPORT:
            return 'Employee Performance Report';
        case NOTIFICATION_TYPE.REMINDER_EVENT:
        case NOTIFICATION_TYPE.REMINDER_CALL:
        case NOTIFICATION_TYPE.REMINDER_TASK:
            return `You have a ${recordType} reminder`;
        default:
            return recordType;
        }
    };

    const getNotificationBody = ({
        itemType,
        customerName,
        recordType,
        realizationDate,
        opportunityCode,
        message,
    }) => {
        switch (itemType) {
        case NOTIFICATION_TYPE.CREATE_REPORT:
            return 'A new report has been created';
        case NOTIFICATION_TYPE.OPPORTUNITY_ASSIGN:
            return `The opportunity #${opportunityCode} from ${customerName} has been assigned to you`;
        case NOTIFICATION_TYPE.OPPORTUNITY_UPDATE:
            return `The opportunity #${opportunityCode} from ${customerName} has received a new lead`;
        case NOTIFICATION_TYPE.REMINDER_EVENT:
        case NOTIFICATION_TYPE.REMINDER_CALL:
        case NOTIFICATION_TYPE.REMINDER_TASK:
            return `The ${recordType} with ${customerName} start at ${realizationDate}`;
        default:
            return message;
        }
    };

    const getNotificationLink = (itemType, opportunityId) => {
        switch (itemType) {
        case NOTIFICATION_TYPE.CREATE_REPORT:
            return `/${modules.EMPLOYEE_PERFORMANCE_REPORTS}`;
        default:
            return `/${modules.OPPORTUNITIES}/${opportunityId}`;
        }
    };

    const title = isCommunication ? 'Messages' : 'Notifications';
    return (
        <div>
            <Tooltip title={isCommunication ? 'Messages' : 'Reminders'}>
                <IconButton aria-describedby={id} onClick={handleClick} className={classes.button}>
                    <Badge
                        badgeContent={(() => {
                            const releaseCount = unreadAppReleases.length > 0 ? 1 : 0;
                            return state.notificationCount + collectionCount + (isCommunication ? 0 : releaseCount);
                        })()}
                        color="secondary"
                        children={isCommunication ? <MailOutlineOutlinedIcon /> : <NotificationsOutlinedIcon />}
                    />
                </IconButton>
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 45,
                }}
                classes={{
                    paper: classes.popover,
                }}
            >
                <div className={classes.header}>
                    <Typography variant="h5">{title}</Typography>
                </div>
                <Divider className={classes.divider} />
                <List className={classes.notificationList}>
                    {!isCommunication && unreadAppReleases.length > 0 && (
                        <ListItem
                            button
                            key="consolidated-app-release"
                            onClick={() => {
                                setOpenAppReleaseDialog(true);
                                setAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                <NewReleasesIcon style={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={(
                                    <div className={classes.bodyMessage}>
                                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            New system update.
                                        </Typography>
                                        <Typography className={classes.textMessage}>
                                            {uniqueAppReleaseTypes.join(', ')}
                                        </Typography>
                                    </div>
                                )}
                                secondary={moment.utc(latestAppRelease.modifiedOn || latestAppRelease.releaseDate).local().fromNow()}
                            />
                        </ListItem>
                    )}
                    {collectionCount > 0 && (
                        <RouterLink
                            to={`/${modules.COLLECTIONS}/${COLLECTIONS_SUBMODULES.MESSAGE_CENTER}`}
                            className={classes.link}
                        >
                            <ListItem button onClick={handleClose}>
                                <ListItemIcon>
                                    <ForumOutlinedIcon />
                                </ListItemIcon>
                                <ListItemText
                                    classes={{ primary: classes.textPrimary }}
                                    primary={(
                                        <div className={classes.bodyMessage}>
                                            <Typography variant="h6">
                                                {`Collection message center (${collectionCount})`}
                                            </Typography>
                                        </div>
                                    )}
                                />
                            </ListItem>
                        </RouterLink>
                    )}
                    {state.records.map((item, index) => {
                        const record = JSON.parse(item?.data || '{}');
                        const shouldCapitalizeTitle = [NOTIFICATION_TYPE.OPPORTUNITY_ASSIGN, NOTIFICATION_TYPE.OPPORTUNITY_UPDATE].includes(item?.type);
                        const recordType = record?.type?.toLowerCase() || '';
                        const customerName = record?.customerName || 'customer';
                        const createdOn = DateUtils.fromNow(item.createdOn);
                        const realizationDate = record?.realizationDate ? DateUtils.format(record.realizationDate, DateFormat.DATETIME_WITHOUT_SECONDS) : '';
                        const isPerformanceReport = item?.type === NOTIFICATION_TYPE.CREATE_REPORT;
                        const titleItem = getNotificationTitle({
                            itemType: item?.type,
                            isCommunicationType: isCommunication,
                            customerName,
                            recordType,
                        });
                        const bodyMessage = getNotificationBody({
                            itemType: item?.type,
                            customerName,
                            opportunityCode: record?.opportunityCode,
                            recordType,
                            realizationDate,
                            message: record?.message,
                        });
                        const linkTo = getNotificationLink(item?.type, record?.opportunityId);
                        const notificationKey = item.notificationId || item.userNotification?.notificationId || index;

                        return (
                            <RouterLink
                                to={linkTo}
                                className={classes.link}
                                key={notificationKey}
                            >
                                <ListItem
                                    button
                                    onClick={() => onClick({ ...item, data: record }, isPerformanceReport)}
                                >
                                    <ListItemIcon>
                                        {getIcon(item.type)}
                                    </ListItemIcon>
                                    <ListItemText
                                        classes={{ primary: classes.textPrimary }}
                                        primary={(
                                            <>
                                                <div className={classes.bodyMessage}>
                                                    <Typography variant="h6" className={shouldCapitalizeTitle ? classes.titleItem : ''}>
                                                        {titleItem}
                                                    </Typography>
                                                    <Typography className={classes.textMessage}>{bodyMessage}</Typography>
                                                </div>
                                                <div>
                                                    {!item.userNotification?.read && <span className={classes.messageUnread} />}
                                                </div>
                                            </>
                                        )}
                                        secondary={(
                                            <Typography className={classes.footerItem}>
                                                <AccessTimeIcon
                                                    fontSize="small"
                                                />
                                                <span>{createdOn}</span>
                                            </Typography>
                                        )}
                                    />
                                </ListItem>
                            </RouterLink>
                        );
                    })}
                    <If condition={emptyList}>
                        <ListItem>
                            <ListItemIcon>
                                <InboxIcon />
                            </ListItemIcon>
                            <ListItemText
                                classes={{}}
                                primary={(
                                    <div>
                                        <Typography className={classes.alignCenter}>
                                            {`You don't have ${title}`}
                                        </Typography>
                                    </div>
                                )}
                            />
                        </ListItem>
                    </If>
                </List>
                <Divider className={classes.divider} />
                <div className={classes.footer}>
                    <Button onClick={markAllAsRead} disabled={updating || emptyList}>Mark all as read</Button>
                </div>
            </Popover>
            <AppReleaseDialog
                open={openAppReleaseDialog}
                releases={unreadAppReleases}
                onClose={() => setOpenAppReleaseDialog(false)}
                onMarkAsRead={() => refetchAppReleases()}
            />
        </div>
    );
};

NotificationBadge.propTypes = {
    type: PropTypes.string.isRequired,
    iconColor: PropTypes.string,
    collectionCount: PropTypes.number,
};

NotificationBadge.defaultProps = {
    iconColor: 'white',
    collectionCount: 0,
};

export { TYPE };
export default NotificationBadge;
